import BBreadcrumb, { BreadcrumbItem } from 'components/common/BBreadcrumb';
import { FunctionComponent, useMemo } from 'react';
import VehicleList from './VehicleList';
import VehicleListEmptyPlaceholder from './VehicleListEmptyPlaceholder';
import VehicleListTitle from './VehicleListTitle';
import { VehicleListSortOption } from 'enums/vehicleListSortOption';
import BSelect from 'components/Form/BSelect';
import VehicleListFilter from './VehicleListFilter';
import { useVehicleList } from 'hooks/useVehicleList';
import {
  isSearchResponseExperience,
  PaginatedExperienceWithBreadcrumbs,
  PaginatedVehicleWithBreadcrumbs,
} from 'types/search.type';
import { useSearchState } from 'states/useSearchState';
import BBadge from 'components/common/BBadge';
import { CategoryTypes, linkService } from 'services/LinkService';
import { useLocale } from 'hooks/useLocale';
import { Entries } from 'types/types';
import TranslatedLink from 'components/common/TranslatedLink';
import { harborMappingService } from 'services/HarborMappingService';
import { slugifyText } from 'utils/slugify';

interface VehicleListWrapperProps {
  title: string;
  apiVehicles:
    | PaginatedVehicleWithBreadcrumbs
    | PaginatedExperienceWithBreadcrumbs;
  breadcrumbItems: BreadcrumbItem[];
  defaultVehicleType: CategoryTypes;
}

const VehicleListWrapper: FunctionComponent<VehicleListWrapperProps> = ({
  title,
  apiVehicles,
  breadcrumbItems,
  defaultVehicleType,
}) => {
  const vehicleType = linkService.getRealVehicleCategory(defaultVehicleType);
  const { locale } = useLocale();

  let content, last, apiTotalElements;

  if (isSearchResponseExperience(apiVehicles)) {
    content = apiVehicles.experiences.content;
    last = apiVehicles.experiences.last;
    apiTotalElements = apiVehicles.experiences.totalElements;
  } else {
    content = apiVehicles.vehicles.content;
    last = apiVehicles.vehicles.last;
    apiTotalElements = apiVehicles.vehicles.totalElements;
  }

  const {
    vehicleList,
    incrementPage,
    isThereMorePage,
    setSortOption,
    sortOption,
    sortOptions,
    totalElements,
  } = useVehicleList(content ?? [], last ?? true, apiTotalElements, {
    defaultVehicleType: vehicleType,
    isExperience: defaultVehicleType === 'EXPERIENCE',
  });

  const { allSearchData } = useSearchState();

  const locations = useMemo(() => {
    const linkCategory: typeof linkService.categoryLinks[number] =
      (Object.entries(linkService.categoryMap) as Entries<
        typeof linkService.categoryMap
      >)!.find((a) => a[1] === defaultVehicleType)![0];
    const link = linkService.categoryLinkTranslations.find(
      (a) => a.tr === linkCategory
    )?.[locale];

    const linkTitle =
      linkService.categoryLinkTitleTranslations[linkCategory][locale];

    if (apiVehicles.location) {
      const data: { name: string; link: string }[] = [];
      if (apiVehicles.location.matchType === 'harbor') {
        const harborName = apiVehicles.location.harborName;
        const nearbyHarbors = harborMappingService.getNearbyHarbors(harborName);

        data.push(
          ...nearbyHarbors.map((nearbyHarbor) => ({
            name: `${nearbyHarbor} ${linkTitle}`,
            link: linkService.generateCategoryLink({
              category: link,
              locale,
              locationSlug: slugifyText(nearbyHarbor),
            }),
          }))
        );
      }
      if (
        ['region', 'city', 'country'].includes(apiVehicles.location.matchType)
      ) {
        const slug =
          apiVehicles.location[`${apiVehicles.location.matchType}Slug`];
        console.log('qweqwe');
        console.log('slug', slug);
        console.log('allSearchData', allSearchData);
        console.log(allSearchData.find((a) => a.slug === slug));
        data.push(
          ...(allSearchData
            .find((a) => a.slug === slug)
            ?.children?.filter((a) => a.slug !== slug)
            ?.map((a) => ({
              name: `${a.name} ${linkTitle}`,
              link: linkService.generateCategoryLink({
                category: link,
                locale,
                locationSlug: a.slug,
              }),
            })) || [])
        );
      }
      return data;
    } else {
      return allSearchData.reduce((acc, item) => {
        const data = {
          name: `${item.name} ${linkTitle}`,
          link: linkService.generateCategoryLink({
            category: link,
            locale,
            locationSlug: item.slug,
          }),
        };
        if (!acc.find((a) => a.link === data.link)) {
          acc.push(data);
        }
        return acc;
      }, [] as { name: string; link: string }[]);
    }
  }, [allSearchData, apiVehicles.location, defaultVehicleType, locale]);

  const readableLocation = useMemo(() => {
    if (apiVehicles.location && apiVehicles.location.matchType === 'harbor') {
      return apiVehicles.location.harborName;
    }
    return undefined;
  }, [apiVehicles.location]);

  return (
    <div>
      <BBreadcrumb breadcrumbItems={breadcrumbItems} wrapperClasses="mb-4" />
      <div className="grid grid-cols-6 items-center gap-2">
        <VehicleListTitle
          wrapperClasses="col-span-6 md:col-span-3 row-span-1"
          dataLength={totalElements}
          title={title}
        />
        <div className=" col-span-1 col-end-7 row-start-2 justify-self-end md:row-start-1">
          <BSelect
            className="w-203 self-center"
            options={sortOptions}
            selectedValue={sortOption}
            onChange={(e) =>
              setSortOption(e.target.value as VehicleListSortOption)
            }
          />
        </div>
        {defaultVehicleType !== 'EXPERIENCE' ? (
          <div className="col-span-1 row-start-2 md:col-span-2">
            <VehicleListFilter defaultVehicleType={vehicleType} />
          </div>
        ) : null}
      </div>
      <div className="mt-8">
        {vehicleList.length ? (
          <VehicleList
            vehicles={vehicleList}
            onPageChange={incrementPage}
            isThereMorePage={isThereMorePage}
            location={readableLocation}
          />
        ) : (
          <VehicleListEmptyPlaceholder />
        )}
      </div>
      <div className="mt-12 flex flex-wrap justify-center gap-4">
        {defaultVehicleType !== 'EXPERIENCE' &&
          locations.map((l) => (
            <TranslatedLink key={l.link} href={l.link}>
              <a title={l.name}>
                <BBadge
                  className="border border-transparent text-sm hover:border-slate-900 md:text-base"
                  variant="secondary"
                >
                  {l.name}
                </BBadge>
              </a>
            </TranslatedLink>
          ))}
      </div>
    </div>
  );
};

export default VehicleListWrapper;
